import { SHIP } from "./config.ts";
import { clamp, type Vec3 } from "./math.ts";
import { HULL_BOXES, RAIL_H, SHIP_POINTS } from "./ship.ts";

/**
 * Ship destruction: which parts of a ship can be cut, holed or smashed, where they are
 * (ship-local, matching tools/models/ship.py), what each loss does to sailing, and the
 * packed replicated forms (`ShipState.ropes`, `sailHoles`, `wheelHp`, `breaches`,
 * `CannonState.wrecked`). Server hit tests, client presentation and tests all read this.
 */

const D = SHIP.deckHeight;
const QD = D + SHIP.quarterdeckRise;
const FC = D + SHIP.forecastleRise;

export const DAMAGE = {
  ram: {
    /** Impact speed (m/s, above SHIP.damageImpactSpeed) per extra level of hole severity. */
    severityStep: 2,
    /** The rammer's reinforced stem takes this many severity levels less than the ship it strikes. */
    stemArmour: 1,
    /** From this impact a second hole opens beside the first on the struck side. */
    doubleBreachImpact: 7,
    /** From this impact the struck side's guns, shroud chains and sheets near the contact are torn away. */
    wreckImpact: 6,
    /** Ship-local z distance from the contact within which side fittings are torn away. */
    wreckReach: 1.8,
    /** The rammer's bowsprit snaps its forestay from `wreckImpact`; its fore topmast stay from this impact. */
    topstayImpact: 8,
    /** Fraction of the impact speed the struck hull is shoved away with (it then slows at SHIP.shoveDrag). */
    shove: 0.4,
  },
  /** A hole is 1 (sprung plank) … 4 (stove in); each level floods like one SHIP.floodPerLeak leak and takes one repair. */
  maxSeverity: 4,
  wheelHp: 100,
  /** A round shot through the helm smashes it outright; cutlass hacks take four strikes. */
  ballWheelDamage: 100,
  cutlassWheelDamage: 25,
  /** How close a passing ball's path (centre) or cutlass blade must come to a rope line to part it. */
  ballRopeReach: 0.35,
  bladeRopeReach: 0.3,
  /** Drive a sail loses per shot hole. */
  holeDrag: 0.08,
  /** A sail with this many holes tears from its yard and gives no drive at all. */
  shredHoles: 6,
  /** Fraction of a sail's drive kept with 0, 1 or 2 of its sheets cut. */
  sheetDrive: [1, 0.45, 0.05] as readonly number[],
  /** Drive kept per cut shroud set or stay bracing a sail's mast: a strained rig must be eased. */
  supportDrive: 0.85,
  /** Fraction of drive a fully flooded, waterlogged hull loses. */
  floodDrag: 0.35,
  /** Seconds of held work per repair step. */
  repair: { rope: 3, sailHole: 3, sail: 8, wheel: 5, cannon: 5 },
  /** Planks a smashed helm or dismounted gun takes to rebuild; a damaged helm takes one. */
  planks: { wheel: 2, cannon: 2 },
} as const;

// ------------------------------------------------------------------ rigging

export type RopeKind = "sheet" | "shroud" | "backstay" | "stay";

export interface RopeSpec {
  /** Player-facing name. */
  label: string;
  kind: RopeKind;
  /** Sails whose drive this rope carries: a sheet its own sail, a shroud set or stay every sail on the mast it braces. */
  sails: readonly number[];
  /** Ship-local lines, each [lower anchor, upper anchor]. A sheet's upper end is its sail's clew with the sail fully set. */
  lines: readonly (readonly [Vec3, Vec3])[];
  /** Sheets only: the sail and side whose clew the line's upper end follows as the sail is furled. */
  sheet: { sail: number; side: -1 | 1 } | null;
  /** Ship-local deck spot a sailor stands on to splice it. */
  fix: Vec3;
}

export interface SailSpec {
  label: string;
  /** Head of the sail (its yard line), ship-local. */
  x: number;
  y: number;
  z: number;
  /** Half-width at the head and full depth when set; the leech tapers in by 3.5 % of the drop. */
  half: number;
  drop: number;
  /** Forward belly at mid-depth. */
  billow: number;
  /** Ship-local spot a sailor stands on to patch it. */
  fix: Vec3;
}

const v = (x: number, y: number, z: number): Vec3 => ({ x, y, z });

/** Course, topsail and fore course (tools/models/ship.py `make_sail`). */
export const SAILS: readonly SailSpec[] = [
  { label: "main course", x: 0, y: D + 8.4, z: 1.3, half: 3.6, drop: 5.2, billow: 0.6, fix: v(-0.8, D, 0.8) },
  { label: "main topsail", x: 0, y: D + 14, z: 1.1, half: 2.6, drop: 3.6, billow: 0.42, fix: SHIP_POINTS.nestStand },
  { label: "fore course", x: 0, y: FC + 7.2, z: 7.9, half: 2.8, drop: 4.2, billow: 0.5, fix: v(-0.75, FC, 7.4) },
];

/** Head half-width minus the leech taper at depth fraction t. */
export function sailHalfAt(sail: SailSpec, t: number): number {
  return sail.half - 0.035 * sail.drop * t;
}

/** Vertical scale of a sail set to `level` (0 furled … 1 full); the model's furl. */
export function sailSpan(level: number): number {
  return 0.08 + 0.92 * clamp(level, 0, 1);
}

/** Ship-local clew (lower corner) of `sail` on `side` at furl `level`, written into `out`. */
export function sailClew(sail: number, side: -1 | 1, level: number, out: Vec3): Vec3 {
  const s = SAILS[sail]!;
  out.x = side * sailHalfAt(s, 1);
  out.y = s.y - s.drop * sailSpan(level);
  out.z = s.z;
  return out;
}

const MAIN_X = 0, MAIN_Z = 0.8, FORE_Z = 7.4;
/** ship.py `width(3.0, 3.2)` and `width(8.0, 4.0)`: the hull half-breadth at the main and fore chains. */
const MAIN_CHAIN = 3.0703, FORE_CHAIN = 2.3204;

function shrouds(side: -1 | 1, x: number, y: number, zs: readonly number[], top: Vec3): [Vec3, Vec3][] {
  return zs.map((z) => [v(side * x, y, z), v(side * top.x, top.y, top.z)]);
}
function sheet(label: string, sail: number, side: -1 | 1, deck: Vec3, fix: Vec3): RopeSpec {
  return { label, kind: "sheet", sails: [sail], lines: [[deck, sailClew(sail, side, 1, v(0, 0, 0))]], sheet: { sail, side }, fix };
}
function sideRopes(side: -1 | 1): { sheets: RopeSpec[]; shrouds: RopeSpec[]; backstay: RopeSpec } {
  const name = side < 0 ? "port" : "starboard";
  return {
    sheets: [
      sheet(`${name} main sheet`, 0, side, v(side * 2.92, D + 0.9, -2.1), v(side * 2.2, D, -2.1)),
      sheet(`${name} topsail sheet`, 1, side, v(side * 3.45, D + 8.45, 1.15), SHIP_POINTS.nestStand),
      sheet(`${name} fore sheet`, 2, side, v(side * 2.88, D + 0.9, 5.2), v(side * 2.25, D, 4.95)),
    ],
    shrouds: [
      { label: `${name} main shrouds`, kind: "shroud", sails: [0, 1], sheet: null, fix: v(side * 2.2, D, 3.0),
        lines: shrouds(side, MAIN_CHAIN + 0.24, D + 0.82, [2.7, 3.0, 3.3], v(0.24, D + 8.8, MAIN_Z)) },
      { label: `${name} topmast shrouds`, kind: "shroud", sails: [1], sheet: null, fix: SHIP_POINTS.nestStand,
        lines: shrouds(side, 1.12, D + 9.05, [-0.05, 0.8, 1.65], v(0.12, D + 13.9, MAIN_Z)) },
      { label: `${name} fore shrouds`, kind: "shroud", sails: [2], sheet: null, fix: v(side * 1.75, FC, 7.95),
        lines: shrouds(side, FORE_CHAIN + 0.2, FC + 0.8, [7.6, 7.95, 8.3], v(0.18, FC + 8.4, FORE_Z)) },
    ],
    backstay: { label: `${name} backstay`, kind: "backstay", sails: [1], sheet: null, fix: v(side * 2.3, QD, -6.7),
      lines: [[v(side * 2.95, QD + RAIL_H, -6.6), v(side * 0.1, D + 14.6, MAIN_Z)]] },
  };
}
const PORT = sideRopes(-1), STARBOARD = sideRopes(1);

/**
 * Every cuttable rope; bit i of `ShipState.ropes` set ⇒ ROPES[i] is cut. tools/models/ship.py
 * builds `rope{i}` from these lines, and the client draws it intact or parted.
 */
export const ROPES: readonly RopeSpec[] = [
  PORT.sheets[0]!, STARBOARD.sheets[0]!,
  PORT.sheets[1]!, STARBOARD.sheets[1]!,
  PORT.sheets[2]!, STARBOARD.sheets[2]!,
  PORT.shrouds[0]!, STARBOARD.shrouds[0]!,
  PORT.shrouds[1]!, STARBOARD.shrouds[1]!,
  PORT.shrouds[2]!, STARBOARD.shrouds[2]!,
  PORT.backstay, STARBOARD.backstay,
  { label: "mainstay", kind: "stay", sails: [0, 1], sheet: null, fix: v(0.6, FC, 6.6),
    lines: [[v(0, FC + 0.9, FORE_Z - 0.15), v(MAIN_X, D + 8.7, MAIN_Z + 0.15)]] },
  { label: "main topmast stay", kind: "stay", sails: [1], sheet: null, fix: SHIP_POINTS.nestStand,
    lines: [[v(0, FC + 10.6, FORE_Z), v(MAIN_X, D + 14.6, MAIN_Z + 0.1)]] },
  { label: "forestay", kind: "stay", sails: [2], sheet: null, fix: v(0, FC, 8.6),
    lines: [[v(0, 4.75, 13.9), v(0, FC + 8.4, FORE_Z + 0.1)]] },
  { label: "fore topmast stay", kind: "stay", sails: [2], sheet: null, fix: v(0, FC, 8.6),
    lines: [[v(0, 4.82, 14.4), v(0, FC + 10.7, FORE_Z + 0.08)]] },
];
/** `ROPES` indices of the stays the bowsprit carries (snapped by a hard ram). */
export const FORESTAY = 16;
export const FORE_TOPSTAY = 17;

export const ropeCut = (ropes: number, index: number): boolean => ((ropes >>> index) & 1) === 1;

/** Line `line` of rope `index` with its sail set to `level` (sheets follow the clew), written into `a`/`b`. */
export function ropeLine(index: number, line: number, level: number, a: Vec3, b: Vec3): void {
  const rope = ROPES[index]!, [lo, hi] = rope.lines[line]!;
  a.x = lo.x; a.y = lo.y; a.z = lo.z;
  if (rope.sheet) sailClew(rope.sheet.sail, rope.sheet.side, level, b);
  else { b.x = hi.x; b.y = hi.y; b.z = hi.z; }
}

const closest = { s: 0, t: 0 };
const EPS = 1e-9;

/**
 * Squared distance between segments p0p1 and q0q1; the parameters of the closest points
 * are left in `segmentClosest` (Ericson, Real-Time Collision Detection §5.1.9).
 */
export function segmentDistanceSq(p0: Vec3, p1: Vec3, q0: Vec3, q1: Vec3): number {
  const d1x = p1.x - p0.x, d1y = p1.y - p0.y, d1z = p1.z - p0.z;
  const d2x = q1.x - q0.x, d2y = q1.y - q0.y, d2z = q1.z - q0.z;
  const rx = p0.x - q0.x, ry = p0.y - q0.y, rz = p0.z - q0.z;
  const a = d1x * d1x + d1y * d1y + d1z * d1z, e = d2x * d2x + d2y * d2y + d2z * d2z, f = d2x * rx + d2y * ry + d2z * rz;
  let s = 0, t = 0;
  if (a > EPS || e > EPS) {
    if (a <= EPS) t = clamp(f / e, 0, 1);
    else {
      const c = d1x * rx + d1y * ry + d1z * rz;
      if (e <= EPS) s = clamp(-c / a, 0, 1);
      else {
        const b = d1x * d2x + d1y * d2y + d1z * d2z, denom = a * e - b * b;
        s = denom > EPS ? clamp((b * f - c * e) / denom, 0, 1) : 0;
        t = (b * s + f) / e;
        if (t < 0) { t = 0; s = clamp(-c / a, 0, 1); }
        else if (t > 1) { t = 1; s = clamp((b - c) / a, 0, 1); }
      }
    }
  }
  closest.s = s; closest.t = t;
  const dx = rx + d1x * s - d2x * t, dy = ry + d1y * s - d2y * t, dz = rz + d1z * s - d2z * t;
  return dx * dx + dy * dy + dz * dz;
}
/** Closest-point parameters of the last `segmentDistanceSq` call: `s` on the first segment, `t` on the second. */
export const segmentClosest: Readonly<typeof closest> = closest;

const lineA: Vec3 = { x: 0, y: 0, z: 0 }, lineB: Vec3 = { x: 0, y: 0, z: 0 };
/** Fraction along p0→p1 of the last `nearestRopeHit`. */
export const ropeHit = { along: 0 };

/**
 * The intact rope whose line passes within `reach` of the ship-local segment p0→p1, nearest
 * p0 along it, or -1. `level` is the sail setting (0 furled … 1 full) the sheets follow.
 */
export function nearestRopeHit(ropes: number, level: number, p0: Vec3, p1: Vec3, reach: number): number {
  let best = -1, along = Infinity;
  const r2 = reach * reach;
  for (let i = 0; i < ROPES.length; i++) {
    if (ropeCut(ropes, i)) continue;
    const lines = ROPES[i]!.lines.length;
    for (let k = 0; k < lines; k++) {
      ropeLine(i, k, level, lineA, lineB);
      if (segmentDistanceSq(p0, p1, lineA, lineB) <= r2 && closest.s < along) {
        along = closest.s;
        best = i;
      }
    }
  }
  ropeHit.along = along;
  return best;
}

// ------------------------------------------------------------------ sails

export const SAIL_COLUMNS = 3;
export const SAIL_ROWS = 3;
/** Holes are tracked per cell of a 3×3 grid over each sail; cell = row * 3 + column, row 0 at the yard, column 0 to port. */
export const SAIL_CELLS = SAIL_COLUMNS * SAIL_ROWS;
const SAIL_BITS = 10, HOLE_MASK = (1 << SAIL_CELLS) - 1, SHRED_BIT = 1 << SAIL_CELLS;

/** Hole bitmask (bit = cell) of `sail` in packed `ShipState.sailHoles`. */
export const sailHoles = (packed: number, sail: number): number => (packed >>> (sail * SAIL_BITS)) & HOLE_MASK;
/** The sail has torn from its yard. */
export const sailShredded = (packed: number, sail: number): boolean => ((packed >>> (sail * SAIL_BITS)) & SHRED_BIT) !== 0;
export function sailHoleCount(packed: number, sail: number): number {
  let n = 0;
  for (let bits = sailHoles(packed, sail); bits; bits &= bits - 1) n++;
  return n;
}
export const sailDamaged = (packed: number, sail: number): boolean => ((packed >>> (sail * SAIL_BITS)) & (HOLE_MASK | SHRED_BIT)) !== 0;

const NEIGHBOURS: readonly (readonly number[])[] = Array.from({ length: SAIL_CELLS }, (_, cell) => {
  const r = Math.floor(cell / SAIL_COLUMNS), c = cell % SAIL_COLUMNS, out: number[] = [];
  // A tear runs down and across the cloth before it climbs toward the yard.
  for (const [dr, dc] of [[1, 0], [0, 1], [0, -1], [-1, 0]] as const) {
    const rr = r + dr, cc = c + dc;
    if (rr >= 0 && rr < SAIL_ROWS && cc >= 0 && cc < SAIL_COLUMNS) out.push(rr * SAIL_COLUMNS + cc);
  }
  return out;
});

/**
 * Packed `sailHoles` after a hole in `cell` of `sail`. A hit in an already holed cell tears
 * into an intact neighbour; at DAMAGE.shredHoles holes the sail tears from its yard.
 */
export function holeSail(packed: number, sail: number, cell: number): number {
  const shift = sail * SAIL_BITS;
  let bits = (packed >>> shift) & (HOLE_MASK | SHRED_BIT);
  if (bits & SHRED_BIT) return packed >>> 0;
  let target = cell;
  if (bits & (1 << cell)) {
    target = -1;
    for (const n of NEIGHBOURS[cell]!) if (!(bits & (1 << n))) { target = n; break; }
  }
  if (target >= 0) bits |= 1 << target;
  let count = 0;
  for (let b = bits & HOLE_MASK; b; b &= b - 1) count++;
  if (target < 0 || count >= DAMAGE.shredHoles) bits |= SHRED_BIT;
  return ((packed & ~((HOLE_MASK | SHRED_BIT) << shift)) | (bits << shift)) >>> 0;
}

/** Packed `sailHoles` with `sail` torn from its yard. */
export function shredSail(packed: number, sail: number): number {
  return (packed | ((HOLE_MASK | SHRED_BIT) << (sail * SAIL_BITS))) >>> 0;
}

/** Packed `sailHoles` after one repair step on `sail`: a new sail bent on, or the lowest hole patched. */
export function patchSail(packed: number, sail: number): number {
  const shift = sail * SAIL_BITS;
  const bits = (packed >>> shift) & (HOLE_MASK | SHRED_BIT);
  const next = bits & SHRED_BIT ? 0 : bits & (bits - 1);
  return ((packed & ~((HOLE_MASK | SHRED_BIT) << shift)) | (next << shift)) >>> 0;
}

/** Sail-surface z at across-fraction u (-1 port … 1 starboard) and depth fraction t (0 yard … 1 foot). */
export function sailSurfaceZ(sail: SailSpec, u: number, t: number): number {
  return sail.z + sail.billow * Math.sin(Math.PI * clamp(t, 0, 1)) * (1 - 0.28 * u * u);
}

function sailSide(sail: SailSpec, span: number, p: Vec3): number {
  const t = (sail.y - p.y) / (sail.drop * span);
  const u = clamp(p.x / sailHalfAt(sail, clamp(t, 0, 1)), -1, 1);
  return p.z - sailSurfaceZ(sail, u, t);
}

/** Where the last `sailCrossing` met the cloth: fraction along the segment and ship-local point. */
export const sailHit = { along: 0, x: 0, y: 0, z: 0 };

/**
 * The cell of sail `index`, set to `level`, that the ship-local segment p0→p1 passes through,
 * or -1 when it misses the cloth.
 */
export function sailCrossing(index: number, level: number, p0: Vec3, p1: Vec3): number {
  const sail = SAILS[index]!, span = sailSpan(level), depth = sail.drop * span;
  if ((p0.y > sail.y + 0.3 && p1.y > sail.y + 0.3) || (p0.y < sail.y - depth - 0.3 && p1.y < sail.y - depth - 0.3)) return -1;
  const f0 = sailSide(sail, span, p0), f1 = sailSide(sail, span, p1);
  if ((f0 > 0) === (f1 > 0)) return -1;
  const k = f0 / (f0 - f1);
  const x = p0.x + (p1.x - p0.x) * k, y = p0.y + (p1.y - p0.y) * k, z = p0.z + (p1.z - p0.z) * k;
  const t = (sail.y - y) / depth;
  if (t < 0 || t > 1 || Math.abs(x) > sailHalfAt(sail, t)) return -1;
  sailHit.along = k; sailHit.x = x; sailHit.y = y; sailHit.z = z;
  const column = Math.min(SAIL_COLUMNS - 1, Math.floor(((x / sailHalfAt(sail, t)) + 1) / 2 * SAIL_COLUMNS));
  const row = Math.min(SAIL_ROWS - 1, Math.floor(t * SAIL_ROWS));
  return row * SAIL_COLUMNS + column;
}

/** Relative push of each sail: its cloth area. */
export const SAIL_WEIGHTS: readonly number[] = (() => {
  const areas = SAILS.map((s) => (2 * s.half - 0.035 * s.drop) * s.drop);
  const total = areas.reduce((a, b) => a + b, 0);
  return areas.map((a) => a / total);
})();

/** Fraction (0…1) of its full push sail `index` still gives with this rigging and cloth damage. */
export function sailDrive(index: number, ropes: number, packedHoles: number): number {
  if (sailShredded(packedHoles, index)) return 0;
  let sheets = 0, strained = 0;
  for (let i = 0; i < ROPES.length; i++) {
    if (!ropeCut(ropes, i)) continue;
    const rope = ROPES[i]!;
    if (!rope.sails.includes(index)) continue;
    if (rope.kind === "sheet") sheets++;
    else strained++;
  }
  return Math.max(0, 1 - DAMAGE.holeDrag * sailHoleCount(packedHoles, index)) * DAMAGE.sheetDrive[Math.min(2, sheets)]! * DAMAGE.supportDrive ** strained;
}

/** Fraction (0…1) of its sail-setting speed a ship makes: sail damage weighted by area, slowed by flooding. */
export function shipDrive(s: { ropes: number; sailHoles: number; flooding: number }): number {
  let drive = 0;
  for (let i = 0; i < SAILS.length; i++) drive += SAIL_WEIGHTS[i]! * sailDrive(i, s.ropes, s.sailHoles);
  return drive * (1 - DAMAGE.floodDrag * clamp(s.flooding, 0, 100) / 100);
}

// ------------------------------------------------------------------ helm and guns

/** The helm's wheel as a hit sphere (tools/models/ship.py `wheel` pivot), ship-local. */
export const WHEEL_HIT = { x: 0, y: QD + 0.9, z: -7.35, r: 0.65 } as const;

export const wheelBroken = (s: { wheelHp: number }): boolean => s.wheelHp <= 0;

/** Whether the ship-local segment p0→p1 passes within `r` of point c; the hit fraction is left in `sphereHit.along`. */
export const sphereHit = { along: 0 };
export function segmentSphereHit(p0: Vec3, p1: Vec3, cx: number, cy: number, cz: number, r: number): boolean {
  const dx = p1.x - p0.x, dy = p1.y - p0.y, dz = p1.z - p0.z;
  const len2 = dx * dx + dy * dy + dz * dz;
  const s = len2 > EPS ? clamp(((cx - p0.x) * dx + (cy - p0.y) * dy + (cz - p0.z) * dz) / len2, 0, 1) : 0;
  const ex = p0.x + dx * s - cx, ey = p0.y + dy * s - cy, ez = p0.z + dz * s - cz;
  sphereHit.along = s;
  return ex * ex + ey * ey + ez * ez <= r * r;
}

/** Gun and carriage volume of cannon `index` padded by `pad`: inboard 0.6 m of the trunnions to the run-out muzzle 1 m outboard. */
export function cannonContains(index: number, lx: number, ly: number, lz: number, pad: number): boolean {
  const c = SHIP_POINTS.cannons[index]!;
  const out = (lx - c.x) * c.side;
  return out >= -0.6 - pad && out <= 1.0 + pad && Math.abs(lz - c.z) <= 0.45 + pad && ly >= D - pad && ly <= D + 1.35 + pad;
}

/**
 * Dismounted barrel of a wrecked gun (tools/models/ship.py `FALLEN`): breech on the deck,
 * chase on the port sill. Elevation lifts the muzzle, slew swings it toward the bow, roll
 * turns the barrel about its bore; mirrored to port.
 */
export const CANNON_FALLEN = { elevation: 0.46, slew: 0.1, roll: 0.6 } as const;

/**
 * Babylon rotation (x pitch, y yaw, z roll) of the `cannon{index}` pivot laid at the model's
 * `cannonFallen{index}` position, written into `out`; replaces the aim rotation.
 */
export function cannonFallenRotation(index: number, out: Vec3): Vec3 {
  const side = SHIP_POINTS.cannons[index]!.side;
  out.x = -CANNON_FALLEN.elevation;
  out.y = side * (Math.PI / 2 - CANNON_FALLEN.slew);
  out.z = side * CANNON_FALLEN.roll;
  return out;
}

// ------------------------------------------------------------------ hull

/** Severity (0 sound … DAMAGE.maxSeverity) of leak slot `slot`; `breaches` packs 2 bits of extra severity per slot. */
export function breachSeverity(leaks: number, breaches: number, slot: number): number {
  return leaks & (1 << slot) ? 1 + ((breaches >> (slot * 2)) & 3) : 0;
}

/** Total hole severity: each level floods like one leak. */
export function floodHoles(leaks: number, breaches: number): number {
  let n = 0;
  for (let i = 0; i < SHIP.leakSlots; i++) n += breachSeverity(leaks, breaches, i);
  return n;
}

/** Opens or widens hole `slot` by `levels`; returns the severity gained (0 when already stove in). */
export function widenBreach(s: { leaks: number; breaches: number }, slot: number, levels: number): number {
  const before = breachSeverity(s.leaks, s.breaches, slot);
  const after = Math.min(DAMAGE.maxSeverity, before + Math.max(0, Math.floor(levels)));
  if (after === before) return 0;
  s.leaks |= 1 << slot;
  s.breaches = (s.breaches & ~(3 << (slot * 2))) | ((after - 1) << (slot * 2));
  return after - before;
}

/** One repair step on hole `slot`: narrows it by a level, closing it from severity 1. Returns the severity left. */
export function narrowBreach(s: { leaks: number; breaches: number }, slot: number): number {
  const before = breachSeverity(s.leaks, s.breaches, slot);
  if (before <= 1) {
    s.leaks &= ~(1 << slot);
    s.breaches &= ~(3 << (slot * 2));
    return 0;
  }
  s.breaches = (s.breaches & ~(3 << (slot * 2))) | ((before - 2) << (slot * 2));
  return before - 1;
}

/** The leak slot nearest ship-local (lx, lz), preferring the struck side. */
export function nearestLeakSlot(lx: number, lz: number): number {
  let best = 0, distance = Infinity;
  for (let i = 0; i < SHIP_POINTS.leaks.length; i++) {
    const p = SHIP_POINTS.leaks[i]!;
    const d = Math.hypot(lx - p.x, lz - p.z) + (Math.sign(p.x) !== Math.sign(lx || 1) ? 2 : 0);
    if (d < distance) { distance = d; best = i; }
  }
  return best;
}

/** Hole severity a ram at `impact` m/s gives the struck ship (0 below SHIP.damageImpactSpeed). */
export function ramSeverity(impact: number): number {
  if (impact < SHIP.damageImpactSpeed) return 0;
  return Math.min(DAMAGE.maxSeverity, 1 + Math.floor((impact - SHIP.damageImpactSpeed) / DAMAGE.ram.severityStep));
}

const RAIL_BAND = 0.35;
/**
 * Whether a ball at ship-local (lx, ly, lz) strikes the hull: below a deck top anywhere over the
 * hull, or within the bulwark band at the ship's edge up to the rail. Shot that clears the rail
 * crosses the open deck (where it can strike the helm, guns and crew) until it lands on a deck
 * or meets the far side's bulwark.
 */
export function hullStrike(lx: number, ly: number, lz: number): boolean {
  if (ly < -SHIP.draft) return false;
  for (const b of HULL_BOXES) {
    if (lx < b.minX || lx > b.maxX || lz < b.minZ || lz > b.maxZ) continue;
    if (ly <= b.y1) return true;
    const half = lz > 6 ? 2.2 : 3;
    if (ly <= b.y1 + RAIL_H && (Math.abs(lx) > half - RAIL_BAND || lz > 9 - RAIL_BAND || lz < -9.5 + RAIL_BAND)) return true;
  }
  return false;
}

// ------------------------------------------------------------------ repairs

/** The damage fields `repairStep` reads (a `ShipState`). */
export interface RepairableShip {
  leaks: number;
  ropes: number;
  sailHoles: number;
  wheelHp: number;
  cannons: { readonly [index: number]: { wrecked: boolean } | undefined };
}

/** One held repair step: where the sailor stands (ship-local), how long it takes and the planks it uses. */
export interface RepairStep {
  point: Vec3;
  seconds: number;
  planks: number;
}

/**
 * The next repair step of hold target `target` (`leak:<i>`, `wheel`, `cannon:<i>`, `rope:<i>`,
 * `sail:<i>`) on ship `s`, written into `out`; null when that part needs no repair. A hole closes
 * one severity level per step; a holed sail is patched one hole per step, a torn-away one re-bent whole.
 */
export function repairStep(s: RepairableShip, target: string, out: RepairStep): RepairStep | null {
  const colon = target.indexOf(":"), index = colon < 0 ? -1 : Number(target.slice(colon + 1));
  if (target === "wheel") {
    if (s.wheelHp >= DAMAGE.wheelHp) return null;
    out.point = SHIP_POINTS.wheel; out.seconds = DAMAGE.repair.wheel; out.planks = s.wheelHp <= 0 ? DAMAGE.planks.wheel : 1;
  } else if (target.startsWith("leak:")) {
    const point = SHIP_POINTS.leaks[index];
    if (!point || !(s.leaks & (1 << index))) return null;
    out.point = point; out.seconds = SHIP.repairSeconds; out.planks = 1;
  } else if (target.startsWith("cannon:")) {
    const mount = SHIP_POINTS.cannons[index];
    if (!mount || !s.cannons[index]?.wrecked) return null;
    out.point = mount.point; out.seconds = DAMAGE.repair.cannon; out.planks = DAMAGE.planks.cannon;
  } else if (target.startsWith("rope:")) {
    const rope = ROPES[index];
    if (!rope || !ropeCut(s.ropes, index)) return null;
    out.point = rope.fix; out.seconds = DAMAGE.repair.rope; out.planks = 0;
  } else if (target.startsWith("sail:")) {
    const sail = SAILS[index];
    if (!sail || !sailDamaged(s.sailHoles, index)) return null;
    out.point = sail.fix; out.seconds = sailShredded(s.sailHoles, index) ? DAMAGE.repair.sail : DAMAGE.repair.sailHole; out.planks = 0;
  } else return null;
  return out;
}

/** Player-facing name of ship gun `index`: side and position (bow, waist, quarter). */
export function gunLabel(index: number): string {
  const mount = SHIP_POINTS.cannons[index]!;
  return `${mount.side < 0 ? "port" : "starboard"} ${["bow", "waist", "quarter"][index >> 1]} gun`;
}
